'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface SignUpFormData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  company_name: string;
  company_type: string;
  country: string;
  primary_use_case: string;
  agree_terms: boolean;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

const companyTypes = [
  { value: 'fintech', label: 'Fintech' },
  { value: 'payment_company', label: 'Payment Company' },
  { value: 'bank', label: 'Bank' },
  { value: 'insurtech', label: 'Insurtech' },
  { value: 'other', label: 'Other' },
];

const countries = [
  { value: 'GB', label: 'United Kingdom' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'IE', label: 'Ireland' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'PL', label: 'Poland' },
  { value: 'ES', label: 'Spain' },
  { value: 'SE', label: 'Sweden' },
  { value: 'AT', label: 'Austria' },
  { value: 'BE', label: 'Belgium' },
  { value: 'FI', label: 'Finland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'OTHER', label: 'Other' },
];

const useCases = [
  { value: 'refund_governance', label: 'Refund Governance', description: 'Enforce policy on refund decisions' },
  { value: 'credit_governance', label: 'Credit Governance', description: 'Enforce policy on credit and adjustment actions' },
  { value: 'payment_governance', label: 'Payment Governance', description: 'Enforce policy on payment and payout decisions' },
  { value: 'compliance_controls', label: 'Compliance Controls', description: 'Enforce operational and compliance policies' },
];

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    setError,
    formState: { errors },
  } = useForm<SignUpFormData>({
    defaultValues: {
      primary_use_case: 'refund_governance',
      company_type: 'fintech',
      country: 'GB',
    },
  });

  const watchUseCase = watch('primary_use_case');

  const handleStep1Next = async () => {
    const valid = await trigger(['first_name', 'last_name', 'email', 'password']);
    if (valid) setStep(2);
  };

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);

    try {
      // Step 1: Call server-side signup API (uses service role key — never exposed to browser)
      // This atomically creates: organization → auth user → users record
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          first_name: data.first_name,
          last_name: data.last_name,
          company_name: data.company_name,
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        if (result.error === 'EMAIL_EXISTS') {
          setError('email', { message: result.message });
        } else {
          toast.error(result.error || 'Failed to create account. Please try again.');
        }
        setIsLoading(false);
        return;
      }

      // Step 2: Sign in with the newly created credentials to establish a browser session
      const supabase = createClient();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (signInError) {
        // Account was created but auto-login failed — redirect to login
        toast.success(`Organisation created — please sign in, ${data.first_name}!`);
        onSwitchToLogin();
        setIsLoading(false);
        return;
      }

      toast.success(`Organisation created — welcome to Qelotio, ${data.first_name}!`);
      router.push('/');
      router.refresh();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'An unexpected error occurred');
      setIsLoading(false);
    }
  };

  return (
    <div className="fade-in space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[22px] font-bold text-foreground">
            {step === 1 ? 'Create your account' : 'Set up your organisation'}
          </h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            {step === 1 ? 'Start enforcing business policy in minutes' : 'Tell us about your company'}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-colors ${
            step >= 1 ? 'bg-primary border-primary text-primary-foreground' : 'border-border text-muted-foreground'
          }`}>1</div>
          <div className={`w-8 h-px transition-colors ${step >= 2 ? 'bg-primary' : 'bg-border'}`} />
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-colors ${
            step >= 2 ? 'bg-primary border-primary text-primary-foreground' : 'border-border text-muted-foreground'
          }`}>2</div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="first_name" className="block text-[12px] font-medium text-foreground mb-1.5">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="first_name"
                  type="text"
                  autoComplete="given-name"
                  suppressHydrationWarning
                  {...register('first_name', { required: 'First name is required' })}
                  className={`input-base w-full px-3 py-2.5 text-[13px] ${errors.first_name ? 'border-red-500/60' : ''}`}
                  placeholder="Jana"
                />
                {errors.first_name && (
                  <p className="mt-1 text-[11px] text-red-400">{errors.first_name.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="last_name" className="block text-[12px] font-medium text-foreground mb-1.5">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="last_name"
                  type="text"
                  autoComplete="family-name"
                  suppressHydrationWarning
                  {...register('last_name', { required: 'Last name is required' })}
                  className={`input-base w-full px-3 py-2.5 text-[13px] ${errors.last_name ? 'border-red-500/60' : ''}`}
                  placeholder="Müller"
                />
                {errors.last_name && (
                  <p className="mt-1 text-[11px] text-red-400">{errors.last_name.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="signup-email" className="block text-[12px] font-medium text-foreground mb-1.5">
                Work Email <span className="text-red-500">*</span>
              </label>
              <input
                id="signup-email"
                type="email"
                autoComplete="email"
                suppressHydrationWarning
                {...register('email', {
                  required: 'Work email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
                className={`input-base w-full px-3 py-2.5 text-[13px] ${errors.email ? 'border-red-500/60' : ''}`}
                placeholder="jana@company.com"
              />
              {errors.email && (
                <p className="mt-1 text-[11px] text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="signup-password" className="block text-[12px] font-medium text-foreground mb-1.5">
                Password <span className="text-red-500">*</span>
              </label>
              <p className="text-[11px] text-muted-foreground mb-1.5">Minimum 12 characters with uppercase, number and symbol</p>
              <div className="relative">
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  suppressHydrationWarning
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 12, message: 'Password must be at least 12 characters' },
                    pattern: {
                      value: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/,
                      message: 'Must include uppercase letter, number, and symbol',
                    },
                  })}
                  className={`input-base w-full px-3 py-2.5 pr-10 text-[13px] ${errors.password ? 'border-red-500/60' : ''}`}
                  placeholder="••••••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-[11px] text-red-400">{errors.password.message}</p>
              )}
            </div>

            <button
              type="button"
              onClick={handleStep1Next}
              className="btn-primary w-full py-2.5 text-[14px]"
            >
              Continue to Organisation Setup
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="company_name" className="block text-[12px] font-medium text-foreground mb-1.5">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                id="company_name"
                type="text"
                suppressHydrationWarning
                {...register('company_name', { required: 'Company name is required' })}
                className={`input-base w-full px-3 py-2.5 text-[13px] ${errors.company_name ? 'border-red-500/60' : ''}`}
                placeholder="Acme Payments Ltd"
              />
              {errors.company_name && (
                <p className="mt-1 text-[11px] text-red-400">{errors.company_name.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="company_type" className="block text-[12px] font-medium text-foreground mb-1.5">
                  Company Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="company_type"
                  {...register('company_type', { required: true })}
                  className="input-base w-full px-3 py-2.5 text-[13px] appearance-none cursor-pointer"
                >
                  {companyTypes.map((t) => (
                    <option key={`ctype-${t.value}`} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="country" className="block text-[12px] font-medium text-foreground mb-1.5">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  id="country"
                  {...register('country', { required: true })}
                  className="input-base w-full px-3 py-2.5 text-[13px] appearance-none cursor-pointer"
                >
                  {countries.map((c) => (
                    <option key={`country-${c.value}`} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-medium text-foreground mb-1.5">
                Primary Use Case <span className="text-red-500">*</span>
              </label>
              <p className="text-[11px] text-muted-foreground mb-2">Select your initial governance workflow — you can add more later</p>
              <div className="space-y-2">
                {useCases.map((uc) => (
                  <label
                    key={`uc-${uc.value}`}
                    className={`flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer transition-all ${
                      watchUseCase === uc.value
                        ? 'border-primary bg-primary/5' : 'border-border bg-muted/20 hover:border-primary/40'
                    }`}
                  >
                    <input
                      type="radio"
                      value={uc.value}
                      {...register('primary_use_case')}
                      className="mt-0.5 accent-primary cursor-pointer"
                    />
                    <div>
                      <p className="text-[13px] font-semibold text-foreground">{uc.label}</p>
                      <p className="text-[11px] text-muted-foreground">{uc.description}</p>
                    </div>
                    {uc.value === 'refund_governance' && (
                      <span className="ml-auto flex-shrink-0 text-[10px] font-semibold bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                        Default
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2.5">
              <input
                id="agree_terms"
                type="checkbox"
                {...register('agree_terms', { required: 'You must agree to the terms to continue' })}
                className="mt-0.5 w-3.5 h-3.5 rounded border-border bg-input accent-primary cursor-pointer"
              />
              <label htmlFor="agree_terms" className="text-[12px] text-muted-foreground cursor-pointer leading-relaxed">
                I agree to Qelotio&apos;s{' '}
                <span className="text-primary hover:text-primary/80 cursor-pointer">Terms of Service</span>
                {' '}and{' '}
                <span className="text-primary hover:text-primary/80 cursor-pointer">Privacy Policy</span>
              </label>
            </div>
            {errors.agree_terms && (
              <p className="text-[11px] text-red-400">{errors.agree_terms.message}</p>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn-ghost px-4 py-2.5 text-[13px] border border-border rounded-lg flex-shrink-0"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary flex-1 py-2.5 text-[14px] flex items-center justify-center gap-2"
                style={{ minHeight: '42px' }}
              >
                {isLoading ? (
                  <>
                    <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                    <span>Creating organisation...</span>
                  </>
                ) : (
                  'Create Organisation'
                )}
              </button>
            </div>
          </div>
        )}
      </form>

      <p className="text-center text-[12px] text-muted-foreground">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary hover:text-primary/80 font-medium transition-colors">
          Sign in
        </button>
      </p>
    </div>
  );
}